import { useSnackbar } from 'notistack';
import React from 'react';
import { useCreateLocation } from '../../../../../../app/ApiGen';
import { useCurrentOrganisation } from '../../../../../../app/CurrentOrganisationProvider';
import { FormReadonly } from '../../../../../../components/Form/components/FormReadonly';
import { WizardConfirmationStep } from '../../../../../../components/Wizard/WizardConfirmationStep';
import { Section, useWizard } from '../../../../../../design-system';
import { getLocationFields } from '../../../../../../utils/locations/FieldDefinitions';
import { getIcpNumber } from '../../../../../../utils/locations/installationConnectionPoint';
import { CreateLocationContext } from '../CreateLocationContext';
import type { CreateLocationWizardState } from '../types';

export const ConfirmationStep: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { onCreated } = React.useContext(CreateLocationContext);

  const { state: untypedState } = useWizard();
  const state = untypedState as CreateLocationWizardState;

  const { organisation } = useCurrentOrganisation();
  const { slug } = organisation ?? { slug: '' };

  const { mutate } = useCreateLocation({ orgSlug: slug });

  const onComplete = React.useCallback(async () => {
    const { address, coordinates, installationPoint, name, timeZone } = state;
    const response = await mutate({
      data: {
        attributes: {
          name,
          address,
          coordinates,
          timeZone,
          isPublic: false,
          icpNumber: getIcpNumber(installationPoint),
        },
        type: 'locations',
      },
    });
    enqueueSnackbar('Location created', { variant: 'success' });
    onCreated({ id: response.data.id, name, address });
  }, [enqueueSnackbar, mutate, onCreated, state]);

  return (
    <WizardConfirmationStep itemBeingCreated="location" onSubmit={onComplete}>
      <Section title="Location details">
        <FormReadonly fields={getLocationFields({ organisationId: organisation?.id })} initialValues={state} />
      </Section>
    </WizardConfirmationStep>
  );
};
